import { Box, Flex, Grid } from '@/components/atoms/Grid'
import { Body2, Tiny2 } from '@/components/atoms/typography'
import {
  ProductRateRow,
  ProductRateType,
  ProductRateTypeData
} from '@/network/graphql.g'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import { additionalRatingOptions } from './additionalRatingOptions'
import { v4 as uuidv4 } from 'uuid'
import { validFor } from '@/theme/validFor'

type AdditionalRatingProps = {
  productRateRow?: ProductRateRow
  rateTypeData?: ProductRateTypeData[]
  inReview?: boolean
}

const AdditionalRatingInfo: FC<AdditionalRatingProps> = ({
  productRateRow,
  rateTypeData,
  inReview
}) => {
  const productRatingOptions = additionalRatingOptions()
  const productKeys = productRatingOptions.map(() =>
    useMemo(() => uuidv4(), [])
  )
  const filledBarWidth = 15

  const rateTypeDataValue = (type: ProductRateType) => {
    const data = rateTypeData?.find((item) => item.rateType === type)
    return data?.rateTotal
  }

  const optionType = (value: string) => {
    switch (value) {
      case 'Size':
        return inReview
          ? productRateRow?.rateSize
          : rateTypeDataValue(ProductRateType.Size)
      case 'Color':
        return inReview
          ? productRateRow?.rateColor
          : rateTypeDataValue(ProductRateType.Color)
      case 'Material':
        return inReview
          ? productRateRow?.rateMaterial
          : rateTypeDataValue(ProductRateType.Material)
      default:
        return 0
    }
  }

  const calculatePercentage = (average: number) => {
    return Math.min(
      Math.max((average - 1) * 50, filledBarWidth / 2),
      100 - filledBarWidth / 2
    )
  }

  return (
    <StyledGrid
      inReview={inReview}
      gridTemplateColumns={{
        desktop: 'repeat(3, 1fr)',
        tablet: '1fr',
        mobile: '1fr'
      }}
    >
      {productRatingOptions.map((option, index) => {
        if (optionType(option.type) !== 0) {
          return (
            <StyledFlex key={productKeys[index]} inReview={inReview}>
              <StyledBody2 mb="6px" mt="5px" inReview={inReview}>
                {option.title}
              </StyledBody2>
              <Box width="100%">
                <Flex justifyContent="space-between">
                  <StyledTiny2 my="4px" inReview={inReview}>
                    {option.low}
                  </StyledTiny2>
                  <StyledTiny2 my="4px" inReview={inReview}>
                    {option.middle}
                  </StyledTiny2>
                  <StyledTiny2 my="4px" inReview={inReview}>
                    {option.high}
                  </StyledTiny2>
                </Flex>
                <RatingBar>
                  <FilledBar
                    average={calculatePercentage(optionType(option.type))}
                    filledBarWidth={filledBarWidth}
                  />
                </RatingBar>
              </Box>
            </StyledFlex>
          )
        }
      })}
    </StyledGrid>
  )
}

export default AdditionalRatingInfo

const StyledGrid = styled(Grid)`
  margin-block: ${(props) => (props.inReview ? '0' : '48px')};
  grid-template-columns: ${(props) => props.inReview && '1fr'};
  grid-template-rows: ${(props) => props.inReview && '1fr'};
  gap: 16px;
  ${validFor.desktop`
    gap: ${(props) => (props.inReview ? '8px' : '48px')};
    grid-template-rows: ${(props) => props.inReview && 'repeat(3, 1fr)'};
`};
`

const StyledFlex = styled(Flex)`
  flex-direction: ${(props) => (props.inReview ? 'row' : 'column')};
`

const StyledBody2 = styled(Body2)`
  min-width: 75px;
  font-size: ${(props) => props.inReview && '12px'};
`

const StyledTiny2 = styled(Tiny2)`
  font-weight: ${(props) => props.inReview && '400'};
`

const RatingBar = styled(Box)`
  position: relative;
  width: 100%;
  height: 4px;
  background-color: ${(props) => props.theme.colors.ratingProgressBarNeutral};
`

const FilledBar = styled(Box)`
  position: absolute;
  left: ${(props) => props.average}%;
  height: 100%;
  background-color: ${(props) => props.theme.colors.onBackground};
  width: ${(props) => props.filledBarWidth}%;
  transform: translateX(-50%);
`
